import React from 'react'
import {
  FloraVariants,
  forwardRef,
  slideDown,
  slideUp,
  styled,
} from '@grupoboticario/flora-react-system'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { AccessibleIcon } from '@radix-ui/react-accessible-icon'
import { Text } from '@grupoboticario/flora-react-text'
import { HTMLFloraProps } from '@grupoboticario/flora-react-utils'

const DEFAULT_TOOLTIP_ICON_LABEL = 'Ícone do balão de informações'

const StyledTooltipContent = styled(TooltipPrimitive.Content, {
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '$backgroundAltSecondary',
  color: '$nonInteractiveAltPredominant',
  minHeight: 48,
  gap: '$2',
  padding: '$4',
  borderRadius: '$small',
  animationDuration: '.3s',
  animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
  zIndex: '1',
  maxWidth: '272px',
  '&[data-side="top"]': { animationName: slideUp },
  '&[data-side="bottom"]': { animationName: slideDown },
  '& svg[data-tooltip-arrow]': {
    fill: '$nonInteractivePredominant',
  },
  '& > svg': {
    flexShrink: 0,
    size: 13,
  },
  variants: {
    variant: {
      alt: {
        backgroundColor: '$backgroundPrimary',
        color: '$nonInteractivePredominant',
        '& svg[data-tooltip-arrow]': {
          fill: '$backgroundPrimary',
        },
      },
    },
  },
})

type Direction = 'top' | 'right' | 'bottom' | 'left'

export type TooltipProps = HTMLFloraProps<'div'> &
  FloraVariants<typeof StyledTooltipContent> &
  TooltipPrimitive.TooltipProps &
  TooltipPrimitive.TooltipContentProps & {
    text?: string
    icon?: React.ReactNode
    iconLabel?: string
    direction?: Direction
    align?: 'start' | 'center' | 'end'
    size?: 'standard' | 'small'
    variant?: 'standard' | 'alt'
    open?: boolean
    showArrow?: boolean
    hasPortal?: boolean
  }

type TooltipTriggerType = {
  trigger: (state: boolean) => void
  children: React.ReactNode
}

const TooltipTrigger = forwardRef<'button', TooltipTriggerType>(
  ({ children, trigger }, forwardedRef) => {
    const TriggerComponent: React.ElementType = TooltipPrimitive.Trigger

    return (
      <TriggerComponent
        ref={forwardedRef}
        type={null}
        asChild
        onFocus={() => trigger(true)}
        onMouseEnter={() => trigger(true)}
        onBlur={() => trigger(false)}
        onMouseLeave={() => trigger(false)}
      >
        {children}
      </TriggerComponent>
    )
  },
)

/**
 * @component
 * @name Tooltip
 * @description Tooltip component
 * @tutorial https://flora.gb.tech/docs/components/core/tooltip
 */
export const Tooltip = forwardRef<'div', TooltipProps>((props, forwardedRef) => {
  const {
    children,
    text = '',
    icon,
    iconLabel = DEFAULT_TOOLTIP_ICON_LABEL,
    direction = 'top',
    align = 'center',
    variant,
    defaultOpen = false,
    showArrow = true,
    hasPortal = false,
    onOpenChange,
    ...restProps
  } = props

  const TooltipContentWrapper = hasPortal ? TooltipPrimitive.Portal : 'div'
  const [opened, setOpened] = React.useState(defaultOpen)

  return (
    <TooltipPrimitive.Provider>
      <TooltipPrimitive.Root open={opened} delayDuration={0} onOpenChange={onOpenChange}>
        <TooltipTrigger trigger={setOpened}>{children}</TooltipTrigger>
        <TooltipContentWrapper>
          <StyledTooltipContent
            {...restProps}
            variant={variant}
            side={direction}
            align={align}
            alignOffset={-18}
          >
            {icon && <AccessibleIcon label={iconLabel}>{icon}</AccessibleIcon>}
            <Text size="exceptionsAuxiliarRegular" dangerouslySetInnerHTML={{ __html: text }} />
            {showArrow && (
              <TooltipPrimitive.Arrow data-tooltip-arrow offset={25} width={16} height={8} />
            )}
          </StyledTooltipContent>
        </TooltipContentWrapper>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
})
